import { useState, useEffect } from "react";
import { ref, get, set } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { useParams } from "react-router";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";
import { BsGeoAlt, BsBuilding, BsClock } from "react-icons/bs";

const INPUT_CLASS =
    "w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0 text-sm";

const ApplyJob = () => {
    const { jobId } = useParams();
    const { currentUser } = useUser();
    const navigate = useMoveTo();

    const [job, setJob] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [alreadyApplied, setAlreadyApplied] = useState(false);
    const [isOwnJob, setIsOwnJob] = useState(false);

    const [form, setForm] = useState({
        name: currentUser?.name || "",
        portfolioLink: "",
        coverMessage: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [jobSnap, appSnap] = await Promise.all([
                    get(ref(database, `Jobs/${jobId}`)),
                    get(ref(database, `JobApplications/${jobId}/${currentUser.uid}`)),
                ]);

                if (jobSnap.exists()) {
                    const jobData = { id: jobId, ...jobSnap.val() };
                    setJob(jobData);
                    setIsOwnJob(jobData.hostId === currentUser.uid);
                }

                if (appSnap.exists()) {
                    setAlreadyApplied(true);
                }
            } catch (e) {
                console.error("ApplyJob fetch error:", e);
            } finally {
                setPageLoading(false);
            }
        };
        fetchData();
    }, [jobId, currentUser.uid]);

    const trimmedPortfolioLink = form.portfolioLink.trim();
    const isValidPortfolioLink = /^https?:\/\/[^\s]+$/i.test(trimmedPortfolioLink);
    const isValid =
        form.name.trim() &&
        trimmedPortfolioLink &&
        isValidPortfolioLink &&
        form.coverMessage.trim();

    const handleSubmit = async () => {
        if (!isValid || submitting) return;
        setSubmitting(true);
        try {
            await set(
                ref(database, `JobApplications/${jobId}/${currentUser.uid}`),
                {
                    name: form.name.trim(),
                    portfolioLink: form.portfolioLink.trim(),
                    coverMessage: form.coverMessage.trim(),
                    applicantId: currentUser.uid,
                    applicantName: currentUser.name,
                    applicantProfilePic: currentUser.profilePic || "",
                    appliedAt: Date.now(),
                }
            );
            window.showToast("Application submitted!", "success");
            navigate(ROUTES.JOBS_FIND);
        } catch (e) {
            console.error("ApplyJob submit error:", e);
            window.showToast("Failed to submit. Please try again.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (pageLoading) {
        return (
            <div className="flex flex-col h-screen bg-white dark:bg-[#1A1A1A] items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#327691]" />
            </div>
        );
    }

    if (!job) {
        return (
            <div className="flex flex-col h-screen bg-white dark:bg-[#1A1A1A] text-black dark:text-white items-center justify-center">
                <p className="text-gray-400 text-sm">Job not found.</p>
                <button
                    onClick={() => navigate(ROUTES.JOBS_FIND)}
                    className="mt-4 text-sm text-[#327691] underline"
                >
                    Back to listings
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
            <MobileHeader title="Apply for Job" isBackBtn />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 overflow-y-auto pb-24 md:pb-5 px-4 pt-5 bg-white dark:bg-[#1A1A1A]">
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex-shrink-0 md:block hidden"
                            aria-label="Back"
                        >
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            Apply for Job
                        </h2>
                        <div className="w-5" />
                    </div>

                    <div className="max-w-lg mx-auto">
                        {/* Job summary */}
                        <div className="p-4 rounded-2xl bg-[#327691]/5 dark:bg-[#327691]/10 border border-[#327691]/20 mb-6">
                            <h3 className="font-bold text-base">{job.title}</h3>
                            <div className="flex items-center gap-1 mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                                <BsBuilding className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{job.companyName || job.hostName}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                                <BsGeoAlt className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{job.location}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                                <BsClock className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{job.timeline}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="font-bold text-[#327691] text-sm">
                                    {job.payRate}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                    {job.role}
                                </span>
                            </div>
                        </div>

                        {isOwnJob ? (
                            <div className="text-center py-8">
                                <p className="text-gray-500 text-sm">
                                    You cannot apply to your own job post.
                                </p>
                                <button
                                    onClick={() => navigate(ROUTES.JOBS_MY_POSTS)}
                                    className="mt-4 text-sm text-[#327691] underline"
                                >
                                    View your job posts
                                </button>
                            </div>
                        ) : alreadyApplied ? (
                            <div className="text-center py-8">
                                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-green-500 text-xl">✓</span>
                                </div>
                                <p className="font-semibold text-green-500">
                                    Application submitted
                                </p>
                                <p className="text-sm text-gray-400 mt-1">
                                    You've already applied to this job.
                                </p>
                                <button
                                    onClick={() => navigate(ROUTES.JOBS_FIND)}
                                    className="mt-4 text-sm text-[#327691] underline"
                                >
                                    Browse more jobs
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <h2 className="text-lg font-semibold">
                                    Your Application
                                </h2>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">
                                        Your Name *
                                    </label>
                                    <input
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                name: e.target.value,
                                            }))
                                        }
                                        placeholder="Full name"
                                        className={INPUT_CLASS}
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">
                                        Portfolio Link *
                                    </label>
                                    <input
                                        value={form.portfolioLink}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                portfolioLink: e.target.value,
                                            }))
                                        }
                                        placeholder="https://yourportfolio.com"
                                        type="url"
                                        required
                                        className={INPUT_CLASS}
                                    />
                                    {trimmedPortfolioLink && !isValidPortfolioLink && (
                                        <p className="mt-1 text-xs text-red-500">
                                            Enter a valid URL starting with http:// or https://
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">
                                        Why are you a great fit? *
                                    </label>
                                    <textarea
                                        value={form.coverMessage}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                coverMessage: e.target.value,
                                            }))
                                        }
                                        placeholder="Tell them what makes you the right person for this job..."
                                        rows={5}
                                        className={`${INPUT_CLASS} resize-none`}
                                    />
                                </div>

                                <button
                                    onClick={handleSubmit}
                                    disabled={!isValid || submitting}
                                    className="w-full py-3.5 rounded-xl bg-[#327691] text-white font-semibold text-sm disabled:opacity-40 transition-all"
                                >
                                    {submitting
                                        ? "Submitting..."
                                        : "Submit Application"}
                                </button>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default ApplyJob;
